import { useEffect, useState } from 'react';
import type { EmailMessage, EmailTemplate, StoredFile } from '@epasu/core';
import { crm } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { RichTextArea } from '@/components/RichTextArea';
import { Input } from '@/components/ui/input';
import { PostfachWahl, useAktivePostfaecher } from '@/components/PostfachWahl';
import { MessageBody } from '@/components/MessageBody';
import { X } from 'lucide-react';

interface Props {
  message: EmailMessage;
  /** Vorbelegung aus einem Entwurf. */
  initialSubject?: string;
  initialBody?: string;
  /** Gesetzt, wenn der Dialog aus einem Entwurf geöffnet wurde. */
  draftId?: string;
  onClose: () => void;
  onSent: () => void;
  /** Nach „Als Entwurf speichern“ — damit Liste und Verlauf ihn gleich zeigen. */
  onDraftSaved?: () => void;
}

/**
 * Antworten auf eine Nachricht.
 *
 * Der Server hängt die Threading-Kopfzeilen an, damit die Antwort im
 * Mailprogramm des Kunden in derselben Unterhaltung landet — hier wird nur
 * Betreff und Text gesammelt.
 */
export function ReplyDialog({
  message,
  initialSubject,
  initialBody,
  draftId,
  onClose,
  onSent,
  onDraftSaved,
}: Props) {
  const [subject, setSubject] = useState(
    () => initialSubject ?? antwortBetreff(message.subject),
  );
  const [body, setBody] = useState(initialBody ?? '');
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [bibliothek, setBibliothek] = useState<StoredFile[]>([]);
  const [anhaenge, setAnhaenge] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Wer an info@ geschrieben hat, bekommt die Antwort von info@ — alles andere
  // sieht beim Empfänger wie eine fremde Mail aus.
  const absender = useAktivePostfaecher(message.accountId);

  useEffect(() => {
    void crm.templates.list().then(setTemplates);
    void crm.files.list({ libraryOnly: true }).then(setBibliothek);
  }, []);

  const [rueckfrage, setRueckfrage] = useState(false);
  const [speichert, setSpeichert] = useState(false);

  // Geschrieben ist, was vom Stand beim Oeffnen abweicht: ein unveraenderter
  // Entwurf muss nicht noch einmal gespeichert werden, ein leeres Feld auch
  // nicht.
  const anfangsBetreff = initialSubject ?? antwortBetreff(message.subject);
  const geaendert =
    body.trim() !== '' && (body !== (initialBody ?? '') || subject !== anfangsBetreff);

  /**
   * Jeder Weg hinaus — daneben klicken, Escape, Kreuz, Abbrechen — laeuft
   * hier durch. Mit geschriebenem Text fragt das Fenster erst, statt ihn
   * stillschweigend wegzuwerfen.
   */
  const schliessen = () => {
    if (busy || speichert) return;
    if (geaendert) setRueckfrage(true);
    else onClose();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (rueckfrage) setRueckfrage(false);
      else schliessen();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const alsEntwurfSpeichern = async () => {
    setSpeichert(true);
    setError(null);
    try {
      if (draftId) {
        await crm.drafts.update(draftId, { subject, body });
      } else {
        // Der Kontakt ergibt sich aus der Bezugsmail — auch wenn sie noch
        // niemandem zugeordnet ist, haengt der Entwurf an ihr.
        await crm.drafts.create({
          replyToMessageId: message.id,
          subject,
          body,
          source: 'user',
        });
      }
      onDraftSaved?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Entwurf konnte nicht gespeichert werden');
      setRueckfrage(false);
      setSpeichert(false);
    }
  };


  const empfaenger =
    message.direction === 'inbound' ? message.fromAddress : message.toAddress;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim()) {
      setError('Die Antwort ist leer.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await crm.mailbox.reply({
        messageId: message.id,
        subject,
        body,
        ...(anhaenge.length ? { fileIds: anhaenge } : {}),
        ...(absender.konto ? { accountId: absender.konto } : {}),
      });
      if (!res.ok) {
        setError(res.error ?? 'Versand fehlgeschlagen');
        setBusy(false);
        return;
      }
      // Ein Entwurf, dessen Mail draußen ist, hat keinen Grund mehr im
      // Verlauf zu stehen. Scheitert nur das Verwerfen, ist die Mail trotzdem
      // raus — deshalb kein Abbruch, der Entwurf lässt sich von Hand entfernen.
      if (draftId) {
        try {
          await crm.drafts.discard(draftId);
        } catch {
          /* absichtlich still */
        }
      }
      onSent();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Versand fehlgeschlagen');
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40 motion-safe:animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label="Antworten"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) schliessen();
      }}
    >
      {/*
        Von rechts statt in der Mitte: links bleibt die Mail stehen, auf die
        geantwortet wird — im Panel selbst noch einmal daneben, damit man beim
        Schreiben Satz fuer Satz darauf Bezug nehmen kann, ohne zu scrollen.
      */}
      <form
        onSubmit={(e) => void submit(e)}
        className="flex h-full w-full min-w-0 max-w-6xl flex-col sm:w-[calc(100%-4rem)] border-l bg-background shadow-lg motion-safe:animate-slide-in-right"
      >
        <div className="flex items-start justify-between gap-3 border-b px-6 py-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">Antworten</h2>
            <p className="truncate text-sm text-muted-foreground">An {empfaenger ?? '—'}</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={schliessen}
            disabled={busy}
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 grid-rows-[auto_1fr] lg:grid-cols-2 lg:grid-rows-1">
          <Bezugsnachricht message={message} />

          <div className="min-h-0 min-w-0 space-y-3 overflow-y-auto px-6 py-4">
            <PostfachWahl
              konten={absender.konten}
              wert={absender.konto}
              onChange={absender.setKonto}
            />

            {templates.length > 0 && (
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">
                  Vorlage einfügen
                </span>
                <select
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  defaultValue=""
                  onChange={(e) => {
                    const t = templates.find((x) => x.id === e.target.value);
                    if (!t) return;
                    // Der Betreff wird nur ersetzt, wenn noch nichts Eigenes
                    // dasteht — sonst überschreibt eine Vorlage versehentlich
                    // eine schon angepasste Zeile.
                    if (!body.trim()) setSubject(t.subject);
                    setBody((b) => (b.trim() ? `${b}\n\n${t.body}` : t.body));
                    e.currentTarget.value = '';
                  }}
                >
                  <option value="">— keine —</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Betreff</span>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
            </label>

            {bibliothek.length > 0 && (
              <AnhangWahl
                dateien={bibliothek}
                gewaehlt={anhaenge}
                onChange={setAnhaenge}
              />
            )}

            <RichTextArea label="Text" value={body} onChange={setBody} />

            {error && (
              <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t px-6 py-3">
          <Button type="button" variant="outline" onClick={schliessen} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? 'Sende …' : 'Senden'}
          </Button>
        </div>
      </form>

      {rueckfrage && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-4"
          role="alertdialog"
          aria-modal="true"
          aria-label="Als Entwurf speichern?"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !speichert) setRueckfrage(false);
          }}
        >
          <div className="w-full max-w-md rounded-lg border bg-background p-6 shadow-lg">
            <h3 className="text-base font-semibold">
              {draftId ? 'Änderungen am Entwurf speichern?' : 'Als Entwurf speichern?'}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Der Entwurf steht danach im Posteingang unter „Entwürfe“ und oben an dieser Mail —
              dort lässt er sich weiterschreiben.
            </p>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                disabled={speichert}
                onClick={() => setRueckfrage(false)}
              >
                Weiterschreiben
              </Button>
              <Button type="button" variant="outline" disabled={speichert} onClick={onClose}>
                {draftId ? 'Änderungen verwerfen' : 'Verwerfen'}
              </Button>
              <Button
                type="button"
                autoFocus
                disabled={speichert}
                onClick={() => void alsEntwurfSpeichern()}
              >
                {speichert ? 'Speichere …' : 'Als Entwurf speichern'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Die Mail, auf die geantwortet wird.
 *
 * Auf breiten Bildschirmen links neben dem Formular und fuer sich scrollbar;
 * auf schmalen oben und einklappbar, damit das Formular nicht unter einem
 * langen Text verschwindet.
 */
function Bezugsnachricht({ message }: { message: EmailMessage }) {
  const [offen, setOffen] = useState(true);
  const zeit = message.receivedAt ?? message.sentAt ?? message.createdAt;

  return (
    <section
      aria-label="Ursprüngliche Nachricht"
      className="min-h-0 min-w-0 border-b bg-muted/30 lg:overflow-y-auto lg:border-b-0 lg:border-r"
    >
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 px-6 py-3 text-left text-xs font-medium text-muted-foreground lg:pointer-events-none"
        onClick={() => setOffen((o) => !o)}
        aria-expanded={offen}
      >
        <span>
          {message.direction === 'inbound' ? 'Nachricht von ' : 'Unsere Nachricht an '}
          <span className="text-foreground">
            {message.direction === 'inbound' ? message.fromAddress : message.toAddress}
          </span>
          {zeit && (
            <>
              {' · '}
              {new Date(zeit).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}
            </>
          )}
        </span>
        <span className="lg:hidden">{offen ? '▴' : '▾'}</span>
      </button>
      <div
        className={`max-h-[35vh] overflow-y-auto px-6 pb-4 lg:block lg:max-h-none lg:overflow-visible ${
          offen ? '' : 'hidden'
        }`}
      >
        <p className="text-sm font-semibold">{message.subject || '(ohne Betreff)'}</p>
        <MessageBody text={message.body} />
      </div>
    </section>
  );
}

/** „Re:“ voranstellen, aber nicht stapeln. */
function antwortBetreff(subject: string | null): string {
  const s = (subject ?? '').trim();
  return /^re:/i.test(s) ? s : `Re: ${s}`;
}

/**
 * Auswahl aus der Dateiablage.
 *
 * Bewusst nur Bibliotheksdateien: Einen eingegangenen Anhang an einen anderen
 * Kunden weiterzuschicken wäre selten gewollt und gelegentlich ein
 * Datenschutzproblem.
 */
export function AnhangWahl({
  dateien,
  gewaehlt,
  onChange,
}: {
  dateien: StoredFile[];
  gewaehlt: string[];
  onChange: (ids: string[]) => void;
}) {
  const [offen, setOffen] = useState(false);

  return (
    <div className="space-y-1.5">
      <button
        type="button"
        className="text-xs font-medium text-muted-foreground hover:text-foreground"
        onClick={() => setOffen((o) => !o)}
      >
        Anhänge {gewaehlt.length > 0 && `(${gewaehlt.length})`} {offen ? '▴' : '▾'}
      </button>

      {offen && (
        <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border p-2">
          {dateien.map((d) => (
            <label key={d.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={gewaehlt.includes(d.id)}
                onChange={(e) =>
                  onChange(
                    e.target.checked
                      ? [...gewaehlt, d.id]
                      : gewaehlt.filter((x) => x !== d.id),
                  )
                }
              />
              <span className="truncate">{d.name}</span>
            </label>
          ))}
        </div>
      )}

      {!offen && gewaehlt.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {dateien
            .filter((d) => gewaehlt.includes(d.id))
            .map((d) => d.name)
            .join(', ')}
        </p>
      )}
    </div>
  );
}
